import * as React from 'react';
import { ValidationProps, AccessCard } from 'ui';
import { FIELD_TYPE } from 'app/infrastructure/simple-form';
import { SimpleFormComponentProps, ApiToUse } from 'app/infrastructure/simple-form/types';
import { withRoleBasedAccess, RoleBasedAccessCustomStateProps } from '../role-based-access';
import { OptionsPanelDialog } from './options-panel-dialog';
import { mapInvalidOptions } from '../shared';
import { ExtraFooterButton } from 'ui/common/select/types';
import { SingleOptionPanelInitialValue, SingleOptionPanelSelectedValue } from 'app/components/access-panel/options-panel/types';
import { hasActiveValues, inactiveValues } from 'app/components/access-panel/options-panel/options-panel.utils';

const valueFieldName = (formName: string) => `${formName}_id`;

interface SingleOptionPanelProps {
  readOnly: boolean;
  active?: boolean;
  translation: any;
  initialValue: SingleOptionPanelInitialValue[];

  formComponent: React.ComponentType<SimpleFormComponentProps>;

  formValues: any;
  formName: string;
  valueName: string;
  validation: ValidationProps;

  valueOptionsEndpoint: string;
  valueLabel: string;
  panelLabel?: string;
  addButtonText: string;
  displaySelectAll?: boolean;

  extraFooterButton?: ExtraFooterButton;
  clearValueInStore: (id: string) => void;
  clearValidation: (id: string) => void;
}

interface SingleOptionPanelState {
  selectedValues: SingleOptionPanelSelectedValue[];
  initialValues?: SingleOptionPanelSelectedValue[];
}

type SingleOptionPanelComponentProps = SingleOptionPanelProps & RoleBasedAccessCustomStateProps<SingleOptionPanelState>;

export class SingleOptionPanelComponent extends React.Component<SingleOptionPanelComponentProps> {

  public componentDidMount() {
    this.buildInitialState();
  }

  public componentDidUpdate() {
    this.buildInitialState();
  }

  private buildInitialState = () => {

    const { initialValue, customState, changeCustomState } = this.props;

    // Check if state does not exist and there is initial value
    if (customState || !this.props.initialValue) {
      return;
    }

    const selectedValues: SingleOptionPanelSelectedValue[] = (initialValue && initialValue.map(v => ({
      value: v.v,
      description: v.d,
      active: v.active
    }))) || [];

    changeCustomState({
      selectedValues,
      initialValues: selectedValues
    });
  };

  private addValues = () => {

    const { formValues, formName, clearValueInStore, clearValidation, customState, changeCustomState } = this.props;
    const { selectedValues = [] } = customState || {};

    const fieldName = valueFieldName(formName);

    if (!formValues[fieldName] || formValues[fieldName].length === 0) {
      return;
    }

    const newValues = formValues[fieldName].filter(v => !selectedValues.some(
      sv => sv.value === v.value
    ));

    clearValueInStore(fieldName);
    clearValidation(formName);

    changeCustomState({
      ...(customState || {}),
      selectedValues: [
        ...selectedValues,
        ...newValues
      ]
    });
  };

  private deleteAccessCard = () => {
    this.props.changeCustomState({
      ...(this.props.customState || {}),
      selectedValues: inactiveValues(this.props.customState.selectedValues)
    });
  };

  private updateValues = (values: SingleOptionPanelSelectedValue[]) => {

    this.props.changeCustomState({
      ...(this.props.customState || {}),
      selectedValues: values
    });
  };

  private removeAll = () => this.updateValues(inactiveValues(this.props.customState.selectedValues));

  public render() {
    const { translation, formName, valueName, validation, readOnly, valueOptionsEndpoint,
      formComponent: SimpleForm, addButtonText, valueLabel, panelLabel, displaySelectAll, extraFooterButton
    } = this.props;
    const { selectedValues = [], initialValues = [] } = this.props.customState || {};
    const activeValues = hasActiveValues(selectedValues);

    const addValuesPanel = (
      <OptionsPanelDialog
        onSubmit={this.addValues}
        addButtonText={addButtonText}
        doneButtonText={translation.done}
        removeAllText={translation.remove_all_section}
        removeAll={activeValues ? this.removeAll : undefined}
        extraFooterButton={extraFooterButton}>
        <SimpleForm
          name={valueFieldName(formName)}
          type={FIELD_TYPE.SELECT_MULTIPLE_HIERARCHY}
          optionsEndpoint={valueOptionsEndpoint}
          placeholder={translation.search}
          descriptionAllLevels={true}
          apiToUse={ApiToUse.ADMINAPI}
          translation={translation}
          label={{ text: valueLabel, required: false }}
          initialValue={this.props.customState && this.props.customState.selectedValues}
          alwaysOpen={true}
          displaySelectAll={displaySelectAll}
        />
      </OptionsPanelDialog>
    );

    const invalidOptions = mapInvalidOptions(validation, valueName);

    const accessCardPanel =
      <div className="row">
        <AccessCard
          componentId={formName}
          className={undefined}
          label={{ text: panelLabel }}
          useLabelOption={true}
          options={selectedValues}
          valueItems={selectedValues}
          initialValueItems={initialValues}
          invalidOptions={invalidOptions}
          removeLabel={translation.remove}
          validation={validation}
          onChange={this.updateValues}
          onDelete={this.deleteAccessCard}
          readOnly={readOnly} />
      </div>;

    return <>
      {!readOnly && addValuesPanel}
      {activeValues
        ? accessCardPanel
        : readOnly && <p className="no-content">{translation.no_access_has_been_granted}</p>}
    </>;
  }
}

export const SingleOptionPanel = withRoleBasedAccess<SingleOptionPanelProps, SingleOptionPanelState>(SingleOptionPanelComponent);
